import React from 'react'
import { connect } from 'react-redux'
import { IdeIntegration } from '../app/context'
import { TraceLine } from './details/stackTrace'
import { ApplicationState } from '../redux/ApplicationState'
import Link from '../helper/link'
import { trackUserEvent } from '../redux/V2/TrackUserEvent/actions'

interface OwnProps {
    traceLine: TraceLine
}

interface DefaultProps {
    style: any
    renderWhenJustFqcn: boolean
    pullRight: boolean
    className: string
}

interface DispatchProps {
    trackUserEvent: typeof trackUserEvent
}

interface StateProps {
    currentIdeIntegration: IdeIntegration
}

type Props = DefaultProps & OwnProps & StateProps & DispatchProps

class IdeIntegrationButtonComponent extends React.Component<Props> {
    public static defaultProps: DefaultProps = {
        style: {},
        renderWhenJustFqcn: false,
        pullRight: true,
        className: '',
    }

    public render() {
        const file = this.props.traceLine.file ?? ''
        const cnPullRight = this.props.pullRight ? `pull-right` : ''
        const cnMain = `${cnPullRight} ${this.props.className}`

        if (file.includes('/vendor')) {
            if (!this.props.traceLine.package || !this.props.traceLine.version) {
                return null
            }
            return (
                <div className={cnMain} style={this.props.style}>
                    <span className="tw-tooltip tw-tooltip-dataText tw-tooltip--bottom" data-tooltip-text={'Open on GitHub'}>
                        <Link
                            onClick={() => this.props.trackUserEvent('openIde')}
                            route={'XhprofUserBundle.IdeIntegration.vendorPackage'}
                            params={{
                                file: this.props.traceLine.file,
                                line: this.props.traceLine.line,
                                package: this.props.traceLine.package,
                                version: this.props.traceLine.version,
                            }}
                        >
                            <img src="/assets/img/github-mark.svg" width="18" height="18" />
                        </Link>
                    </span>
                    {this.renderIdeIntegration()}
                    {this.renderCopyToClipboard()}
                </div>
            )
        }

        return (
            <div className={cnMain} style={this.props.style}>
                {this.renderIdeIntegration()}
                {this.renderCopyToClipboard()}
            </div>
        )
    }

    private renderIdeIntegration() {
        if (!this.props.renderWhenJustFqcn && !this.hasCodeLocation()) {
            return
        }

        // we cant currently open closures in IDE correctly when we don't have a code location.
        if (this.isClosure() && !this.hasCodeLocation()) {
            return null
        }

        if (!this.props.currentIdeIntegration?.ide) {
            return null
        }

        const editorLabels = { phpstorm: 'PHPStorm', vscode: 'VSCode' }
        const url = this.getUrl()

        return this.props.currentIdeIntegration.ide !== 'clipboard' ? (
            <a
                onClick={() => this.props.trackUserEvent('openIde')}
                href={url}
                className="tw-ml-8 color-hover tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                data-tooltip-text={'Open in ' + editorLabels[this.props.currentIdeIntegration.ide]}
            >
                <img src={'/assets/img/' + this.props.currentIdeIntegration.ide + '.svg'} width="18" height="18" />
            </a>
        ) : null
    }

    private hasCodeLocation() {
        return !(this.props.traceLine.relativeFilename === undefined || this.props.traceLine.relativeFilename.length == 0)
    }

    private isClosure() {
        // we cant currently open closures in IDE correctly.
        if (this.props.traceLine.function.includes('{closure') || this.props.traceLine.function.includes('class@anonymous')) {
            return true
        }
        return false
    }

    private renderCopyToClipboard() {
        // we cant copy closures into clipboard, as they don't have a name.
        if (this.isClosure()) {
            return null
        }

        return (
            <a
                onClick={this.copyToClipboard}
                className="tw-ml-8 tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                data-tooltip-text={'Copy Name to Clipboard'}
            >
                <i className="fa fa-copy" style={{ verticalAlign: 'middle', fontSize: '18px' }} />
            </a>
        )
    }

    public copyToClipboard = () => {
        navigator.clipboard.writeText(this.props.traceLine.function)
    }

    private getUrl() {
        switch (this.props.currentIdeIntegration.ide) {
            case 'phpstorm': {
                let path = '&fqn=' + encodeURI(this.props.traceLine.function)
                if (this.props.traceLine.relativeFilename !== undefined && this.props.traceLine.relativeFilename.length > 0) {
                    path = '&path=' + this.props.traceLine.relativeFilename + ':' + this.props.traceLine.line
                }
                return 'jetbrains://php-storm/navigate/reference?project=' + this.props.currentIdeIntegration.projectName + path
            }
            case 'vscode': {
                return (
                    'vscode://file' +
                    this.props.currentIdeIntegration.localPath +
                    (this.props.traceLine.relativeFilename ?? this.props.traceLine.file) +
                    ':' +
                    this.props.traceLine.line
                )
            }
            default:
                return ''
        }
    }
}

export const IdeIntegrationButton = connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (globalState, ownProps): StateProps => {
        return {
            currentIdeIntegration: globalState.tidewaysContext.currentIdeIntegration,
        }
    },
    {
        trackUserEvent: trackUserEvent,
    },
)(IdeIntegrationButtonComponent)
